import { Banner, Box } from '@oresundsbron/bridge-ui';
import { ThirdPartyError, SignupProviderFetch } from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import { map } from 'fp-ts/lib/Array';
import { TFunction, useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { lookup } from 'fp-ts/lib/Record';
import { map as mapO, getOrElse } from 'fp-ts/lib/Option';

interface SignupErrorsProps {
  errors?: SignupProviderFetch['errors'];
}

type TranslatedError = { key: string; message: string };
type Error = {
  message: string;
  licensePlate?: string;
  email?: string;
  serialNo?: string;
};
type Errors = Record<
  ThirdPartyError['message'],
  (t: TFunction, e: Error) => string
>;

const errorMap: Partial<Errors> = {
  REG_EXISTS: (t, e) =>
    t('form.bbasValidation.licensePlate.exist', {
      licensePlate: e.licensePlate,
    }),
  BIZZ_EXISTS: (t, e) =>
    t('form.bbasValidation.bizzNumber.exist', { bizzNumber: e.serialNo }),
  BIZZ_NUMBER_REQUIRED: (t) => t('form.bbasValidation.bizzNumber.required'),
  EMAIL_EXISTS: (t, e) =>
    t('form.bbasValidation.email.exist', { email: e.email }),
  EMAIL_INVALID_FORMAT: (t, e) =>
    t('form.bbasValidation.email.invalid', { email: e.email }),
  PHONE_NUMBER_REQUIRED: (t) => t('form.bbasValidation.phone.required'),
  CONTACT_NAME_LENGTH_MAX_EXCEEDED: (t) =>
    t('form.sections.contactInformation.brobizzValidationError'),
};

const SignupErrors: FC<SignupErrorsProps> = ({ errors }) => {
  const { t } = useTranslation(['privateSignUp', 'common'], {
    nsMode: 'fallback',
  });

  const translatedErrors = useMemo(() => {
    if (!errors) {
      return null;
    }

    return pipe(
      errors,
      map(
        (e): TranslatedError =>
          pipe(
            errorMap,
            lookup(e.message),
            mapO((f) => ({ key: e.message, message: f(t, e) })),
            getOrElse(() => ({
              key: 'UNKNOWN',
              message: t('form.bbasValidation.unknown'),
            }))
          )
      )
    );
  }, [t, errors]);

  if (!translatedErrors) {
    return null;
  }

  return (
    <Box className="col-span-full flex flex-col gap-4 md:col-span-8 md:gap-6">
      {translatedErrors.map((error) => (
        <Banner color="negative" key={error.key}>
          {error.message}
        </Banner>
      ))}
    </Box>
  );
};

export default SignupErrors;
